import { createAsyncThunk, createSlice } from '@reduxjs/toolkit'
import apiRequest from '../../lib/http'
import { normalizeError } from '../../utils/errorHelpers'

export const PAGE_SIZE = 20

export const fetchUnreadCount = createAsyncThunk(
  'notifications/fetchUnreadCount',
  async (_, { rejectWithValue }) => {
    try {
      const response = await apiRequest.get('/notifications/unread-count')
      return { count: response.data.data?.count, fetchedAt: Date.now() }
    } catch (error) {
      return rejectWithValue(normalizeError(error))
    }
  },
  { condition: (_, { getState }) => getState().notifications?.countStatus !== 'loading' }
)

export const fetchNotifications = createAsyncThunk(
  'notifications/fetchNotifications',
  async ({ offset = 0 } = {}, { rejectWithValue }) => {
    try {
      const response = await apiRequest.get('/notifications', { params: { offset, limit: PAGE_SIZE } })
      return response.data.data
    } catch (error) {
      return rejectWithValue(normalizeError(error))
    }
  },
  { condition: (_, { getState }) => getState().notifications?.listStatus !== 'loading' }
)

export const markNotificationsSeen = createAsyncThunk(
  'notifications/markSeen',
  async ({ until }, { rejectWithValue }) => {
    try {
      const response = await apiRequest.post('/notifications/seen', { until })
      return response.data.data
    } catch (error) {
      return rejectWithValue(normalizeError(error))
    }
  }
)

const initialState = {
  unreadCount: 0,
  countFetchedAt: 0,
  countStatus: 'idle',
  items: [],
  meta: null,
  nextOffset: 0,
  listStatus: 'idle',
  error: null,
}

const notificationsSlice = createSlice({
  name: 'notifications',
  initialState,
  reducers: {},
  extraReducers: (builder) => {
    builder
      .addCase(fetchUnreadCount.pending, (state) => {
        state.countStatus = 'loading'
      })
      .addCase(fetchUnreadCount.fulfilled, (state, action) => {
        state.countStatus = 'idle'
        state.unreadCount = Number(action.payload?.count) || 0
        state.countFetchedAt = action.payload?.fetchedAt || 0
      })
      .addCase(fetchUnreadCount.rejected, (state, action) => {
        state.countStatus = 'idle'
        state.error = action.payload ?? null
      })
      .addCase(fetchNotifications.pending, (state) => {
        state.listStatus = 'loading'
        state.error = null
      })
      .addCase(fetchNotifications.fulfilled, (state, action) => {
        const items = Array.isArray(action.payload?.notifications) ? action.payload.notifications : []
        const offset = action.meta.arg?.offset || 0
        const listed = new Set(offset === 0 ? [] : state.items.map((item) => item.id))
        state.items = [...(offset === 0 ? [] : state.items), ...items.filter((item) => !listed.has(item.id))]
        state.nextOffset = offset + items.length
        state.meta = action.payload?.meta || null
        state.listStatus = 'succeeded'
      })
      .addCase(fetchNotifications.rejected, (state, action) => {
        state.listStatus = 'failed'
        state.error = action.payload ?? null
      })
      .addCase(markNotificationsSeen.fulfilled, (state) => {
        state.unreadCount = 0
      })
  },
})

export default notificationsSlice.reducer
